/**
 * Session D contacts mock (design-only). Client-side CSV parser for the Upload
 * flow. Pure and testable: no file I/O here (the caller passes the file text),
 * no network, no writes. Header detection follows the legacy header vocabulary;
 * rows missing a name or a company are counted as skipped. Quoted newlines are
 * supported so the browser preview matches the server classifier.
 */

import { csvCell, sanitizeImportCell } from "./contact-file";

export interface ParsedContact {
  name: string;
  title: string;
  company: string;
  email: string | null;
  linkedin: string | null;
  sourceGroup: string | null;
  dateMet: string | null;
  notes: string | null;
  /** The original CSV line, verbatim, so a filtered subset can be re-committed
   *  to the server without losing any columns. */
  raw: string;
}

export interface ParseResult {
  /** Data rows (excluding the header). total === valid.length + skipped. */
  total: number;
  /** Rows that have both a name and a company. */
  valid: ParsedContact[];
  /** Rows dropped for a missing name or company. */
  skipped: number;
  /** Whether a name (or first/last) column AND a company column were detected. */
  recognized: boolean;
  /** The original header line, verbatim, for rebuilding a filtered CSV. */
  headerLine: string;
}

/** Canonical header key: lowercase and collapse spaces, underscores, and hyphens
 *  away, so first_name, First Name, and first-name all reduce to "firstname". */
function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[\s_-]+/g, "");
}

/** Aliases written naturally; both these and the file's headers are run through
 *  normalizeHeader before matching. `companyAlt` is a fallback used only when no
 *  primary company column is present (so company_name wins over
 *  simplified_company_name). Unlisted columns are ignored silently. */
const FIELD_ALIASES: Record<string, string[]> = {
  name: ["name", "full name", "contact name", "contact"],
  firstName: ["first name", "first", "given name"],
  lastName: ["last name", "last", "surname", "family name"],
  title: ["title", "job title", "position", "role"],
  company: [
    "company",
    "company name",
    "organization",
    "organisation",
    "employer",
    "account",
    "org",
  ],
  companyAlt: ["simplified company name"],
  email: ["email", "e-mail", "email address"],
  linkedin: ["linkedin", "linkedin url", "linkedin profile"],
  sourceGroup: ["event", "source", "met at", "source group", "group"],
  dateMet: ["date met", "met on", "met date", "date"],
  notes: ["notes", "note", "comments", "comment"],
};

const NORMALIZED_ALIASES: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(FIELD_ALIASES).map(([field, aliases]) => [
    field,
    new Set(aliases.map(normalizeHeader)),
  ])
);

/** Split one CSV line into trimmed cells, honoring double-quoted fields (and ""
 *  as an escaped quote). */
function splitRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charAt(i);
    if (inQuotes) {
      if (ch === '"') {
        if (text.charAt(i + 1) === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(sanitizeImportCell(cur));
      cur = "";
    } else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text.charAt(i + 1) === "\n") i += 1;
      row.push(sanitizeImportCell(cur));
      cur = "";
      if (row.some((cell) => cell.length > 0)) rows.push(row);
      row = [];
    } else {
      cur += ch;
    }
  }
  row.push(sanitizeImportCell(cur));
  if (row.some((cell) => cell.length > 0)) rows.push(row);
  return rows;
}

function detectColumns(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  headers.forEach((header, index) => {
    const norm = normalizeHeader(header);
    for (const [field, aliases] of Object.entries(NORMALIZED_ALIASES)) {
      if (map[field] === undefined && aliases.has(norm)) {
        map[field] = index;
        break;
      }
    }
  });
  return map;
}

export function parseCsv(text: string): ParseResult {
  const rows = splitRows(text.replace(/^\ufeff/, ""));
  if (rows.length < 2) {
    return { total: 0, valid: [], skipped: 0, recognized: false, headerLine: "" };
  }

  const headers = rows[0] ?? [];
  const headerLine = headers.map(csvCell).join(",");
  const cols = detectColumns(headers);
  const hasName =
    cols.name !== undefined || cols.firstName !== undefined || cols.lastName !== undefined;
  const hasCompany = cols.company !== undefined || cols.companyAlt !== undefined;
  const recognized = hasName && hasCompany;

  const dataRows = rows.slice(1);
  const valid: ParsedContact[] = [];
  let skipped = 0;

  for (const cells of dataRows) {
    const get = (field: string): string => {
      const idx = cols[field];
      if (idx === undefined) return "";
      return cells[idx]?.trim() ?? "";
    };

    let name = get("name");
    if (!name) {
      name = `${get("firstName")} ${get("lastName")}`.trim();
    }
    const company = get("company") || get("companyAlt");

    if (!name || !company) {
      skipped += 1;
      continue;
    }

    valid.push({
      name,
      title: get("title"),
      company,
      email: get("email") || null,
      linkedin: get("linkedin") || null,
      sourceGroup: get("sourceGroup") || null,
      dateMet: get("dateMet") || null,
      notes: get("notes") || null,
      raw: cells.map(csvCell).join(","),
    });
  }

  return { total: dataRows.length, valid, skipped, recognized, headerLine };
}
